import { useEffect, useMemo, useState } from 'react';

import type { TierGroupChildIdsByParentId } from 'features/offer/utils/personalize-offer-list-items';

import { groqQuery } from 'remote/api/groq';
import { useNetworkContext } from 'state/network';

interface TierGroupChild {
  cmsId: string;
  loyaltyEngineId?: string;
}

interface TierGroupRelationship {
  _id: string;
  children?: TierGroupChild[];
}

interface TierGroupLookupState {
  childIdsByParentId: TierGroupChildIdsByParentId;
  hasError: boolean;
  requestKey: string;
}

interface CachedTierGroupLookup {
  expiresAt: number;
  promise: Promise<TierGroupChildIdsByParentId>;
}

const TIER_GROUP_RELATIONSHIPS_GROQ = `
  *[_type == "systemwideTierGroup" && _id in $ids] {
    _id,
    "children": offers[defined(@)]->{
      "cmsId": _id,
      loyaltyEngineId
    }
  }
`;

const EMPTY_TIER_GROUP_CHILD_IDS: TierGroupChildIdsByParentId = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;
const LOOKUP_TIMEOUT_MS = 1_500;
const MAX_CACHE_ENTRIES = 10;
const tierGroupLookupCache = new Map<string, CachedTierGroupLookup>();

const buildChildIdsByParentId = (
  relationships: TierGroupRelationship[]
): TierGroupChildIdsByParentId => {
  const childIdsByParentId = new Map<string, readonly string[]>();

  for (const relationship of relationships) {
    const childIds = new Set<string>();

    for (const child of relationship.children ?? []) {
      if (child.cmsId) {
        childIds.add(child.cmsId);
      }
      if (child.loyaltyEngineId) {
        childIds.add(child.loyaltyEngineId);
      }
    }

    childIdsByParentId.set(relationship._id, Array.from(childIds));
  }

  return childIdsByParentId;
};

const fetchTierGroupRelationships = (
  endpoint: string,
  ids: string[],
  cacheKey: string
): Promise<TierGroupChildIdsByParentId> => {
  const now = Date.now();
  const cachedLookup = tierGroupLookupCache.get(cacheKey);

  if (cachedLookup && cachedLookup.expiresAt > now) {
    return cachedLookup.promise;
  }

  if (cachedLookup) {
    tierGroupLookupCache.delete(cacheKey);
  }

  const promise = groqQuery<TierGroupRelationship[], { ids: string[] }>(
    endpoint,
    TIER_GROUP_RELATIONSHIPS_GROQ,
    { ids },
    { loggingTag: 'offersPersonalizationTierGroups' }
  )
    .then(relationships => buildChildIdsByParentId(relationships ?? []))
    .catch(error => {
      tierGroupLookupCache.delete(cacheKey);
      throw error;
    });

  if (tierGroupLookupCache.size >= MAX_CACHE_ENTRIES) {
    const oldestCacheKey = tierGroupLookupCache.keys().next().value;
    if (oldestCacheKey) {
      tierGroupLookupCache.delete(oldestCacheKey);
    }
  }

  tierGroupLookupCache.set(cacheKey, {
    expiresAt: now + CACHE_TTL_MS,
    promise,
  });

  return promise;
};

export const useOffersPersonalizationTierGroups = (
  enabled: boolean,
  sortedSystemwideOffers?: string[]
) => {
  const { sanityEndpoints } = useNetworkContext();
  const idsKey = enabled && sortedSystemwideOffers ? JSON.stringify(sortedSystemwideOffers) : '';
  const requestKey = idsKey ? `${sanityEndpoints.groq}:${idsKey}` : '';
  const [lookupState, setLookupState] = useState<TierGroupLookupState>({
    childIdsByParentId: EMPTY_TIER_GROUP_CHILD_IDS,
    hasError: false,
    requestKey: '',
  });

  useEffect(() => {
    if (!requestKey) {
      return;
    }

    let isCurrent = true;
    let timeoutId: ReturnType<typeof setTimeout>;
    const ids = JSON.parse(idsKey) as string[];
    const lookupTimeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('Offers personalization tier-group lookup timed out')),
        LOOKUP_TIMEOUT_MS
      );
    });

    void Promise.race([
      fetchTierGroupRelationships(sanityEndpoints.groq, ids, requestKey),
      lookupTimeout,
    ])
      .then(childIdsByParentId => {
        clearTimeout(timeoutId);

        if (isCurrent) {
          setLookupState({
            childIdsByParentId,
            hasError: false,
            requestKey,
          });
        }
      })
      .catch(() => {
        clearTimeout(timeoutId);

        if (isCurrent) {
          setLookupState({
            childIdsByParentId: EMPTY_TIER_GROUP_CHILD_IDS,
            hasError: true,
            requestKey,
          });
        }
      });

    return () => {
      isCurrent = false;
      clearTimeout(timeoutId);
    };
  }, [idsKey, requestKey, sanityEndpoints.groq]);

  return useMemo(() => {
    if (!requestKey) {
      return {
        childIdsByParentId: EMPTY_TIER_GROUP_CHILD_IDS,
        hasError: false,
        isPending: false,
      };
    }

    const isPending = lookupState.requestKey !== requestKey;

    return {
      childIdsByParentId: isPending ? EMPTY_TIER_GROUP_CHILD_IDS : lookupState.childIdsByParentId,
      hasError: isPending ? false : lookupState.hasError,
      isPending,
    };
  }, [lookupState, requestKey]);
};
